import { Modal, Descriptions, Tag, Button, Typography, message, Alert } from "antd"
import { CopyOutlined, RobotOutlined, UserOutlined } from "@ant-design/icons"
import type React from "react"
import { DeviceBadge } from "@/components/DeviceBadge"
import type { AiLogItem } from "@/services/log/aiLogApi"
import { getAiActionTypeInfo, formatUsdAndVnd } from "@/constants/ai"
import dayjs from "dayjs"

const { Text } = Typography

interface AiLogDetailModalProps {
  open: boolean
  record: AiLogItem | null
  onClose: () => void
}

export const AiLogDetailModal: React.FC<AiLogDetailModalProps> = ({ open, record, onClose }) => {
  if (!record) return null

  const actionInfo = getAiActionTypeInfo(record.action_type)
  const costInfo = formatUsdAndVnd(record.cost)
  const totalTokens = (Number(record.token_input) || 0) + (Number(record.token_output) || 0)
  const isSuccess = record.is_success === 1 || record.is_success === true || record.is_success === undefined

  let formattedPrompt = record.prompt || ""
  let isPromptJson = false

  if (record.prompt) {
    try {
      const parsed = JSON.parse(record.prompt)
      formattedPrompt = JSON.stringify(parsed, null, 2)
      isPromptJson = true
    } catch {
      isPromptJson = false
    }
  }

  let formattedOutput = record.ai_output || ""
  let isJson = false

  if (record.ai_output) {
    try {
      const parsed = JSON.parse(record.ai_output)
      formattedOutput = JSON.stringify(parsed, null, 2)
      isJson = true
    } catch {
      isJson = false
    }
  }

  const handleCopyPrompt = () => {
    if (record.prompt) {
      navigator.clipboard.writeText(record.prompt)
      message.success("Đã sao chép nội dung Prompt vào clipboard!")
    }
  }

  const handleCopyOutput = () => {
    if (record.ai_output) {
      navigator.clipboard.writeText(record.ai_output)
      message.success("Đã sao chép nội dung AI Output vào clipboard!")
    }
  }

  return (
    <Modal
      title={
        <div className="flex items-center gap-2 pr-6">
          <RobotOutlined className="text-lg text-purple-600" />
          <span>Chi tiết Log AI #{record.id}</span>
          <Tag color={actionInfo.color}>{actionInfo.title}</Tag>
          <Tag color="geekblue">{record.model}</Tag>
          {isSuccess ? <Tag color="success">Thành công</Tag> : <Tag color="error">Thất bại</Tag>}
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={[
        record.prompt ? (
          <Button key="copy-prompt" icon={<CopyOutlined />} onClick={handleCopyPrompt}>
            Sao chép Prompt
          </Button>
        ) : null,
        record.ai_output ? (
          <Button key="copy-output" icon={<CopyOutlined />} onClick={handleCopyOutput}>
            Sao chép Output
          </Button>
        ) : null,
        <Button key="close" type="primary" onClick={onClose}>
          Đóng
        </Button>,
      ]}
      width={900}
      style={{ top: 20 }}
      destroyOnClose
    >
      <div className="flex max-h-[78vh] flex-col gap-4 overflow-y-auto py-2 pr-1">
        {!isSuccess && record.error_message && (
          <Alert
            type="error"
            showIcon
            message="Chi tiết lỗi từ AI Provider"
            description={<pre className="whitespace-pre-wrap font-mono text-xs">{record.error_message}</pre>}
          />
        )}

        <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="Trạng thái">
            {isSuccess ? (
              <Tag color="success">Thành công</Tag>
            ) : (
              <Tag color="error">Thất bại / Lỗi</Tag>
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Thời gian">
            {record.created_at ? dayjs(record.created_at).format("DD/MM/YYYY HH:mm:ss") : "—"}
          </Descriptions.Item>

          <Descriptions.Item label="Người dùng">
            {record.user ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <UserOutlined className="text-blue-500" />
                <span className="font-semibold">{record.user.name || record.user.username}</span>
                <Text type="secondary">({record.user.email})</Text>
                <Tag color="blue">#{record.user_id}</Tag>
                {record.user.is_test === 1 && (
                  <Tag color="warning" className="m-0 shrink-0 px-1 py-0 font-semibold text-[10px]">
                    🧪 Test
                  </Tag>
                )}
              </div>
            ) : (
              <Tag color="default">Khách vãng lai (Chưa đăng nhập)</Tag>
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Thiết bị / Nền tảng">
            <DeviceBadge userAgent={record.user_agent} ipAddress={record.ip_address} showDetails />
          </Descriptions.Item>

          {record.ip_address && (
            <Descriptions.Item label="Địa chỉ IP">
              <span className="font-mono text-xs">{record.ip_address}</span>
            </Descriptions.Item>
          )}

          <Descriptions.Item label="Tính năng">
            <div>
              <span className="font-semibold">{actionInfo.title}</span>
              <Text type="secondary" className="block text-xs">
                Mã: {record.action_type}
              </Text>
            </div>
          </Descriptions.Item>

          <Descriptions.Item label="Model AI">
            <Tag color="geekblue" className="font-mono">
              {record.model}
            </Tag>
          </Descriptions.Item>

          {record.prompt_code && (
            <Descriptions.Item label="Prompt Code" span={2}>
              <Tag color="volcano" className="font-mono">
                {record.prompt_code}
              </Tag>
            </Descriptions.Item>
          )}

          <Descriptions.Item label="Tokens tiêu thụ">
            <div className="text-xs">
              <div>
                Input: <strong>{(record.token_input || 0).toLocaleString()}</strong> | Output:{" "}
                <strong>{(record.token_output || 0).toLocaleString()}</strong>
              </div>
              <div className="mt-0.5 font-semibold text-gray-500">Tổng: {totalTokens.toLocaleString()} tokens</div>
            </div>
          </Descriptions.Item>

          <Descriptions.Item label="Chi phí ước tính">
            <div>
              <span className="font-mono font-semibold text-emerald-600">{costInfo.usd}</span>
              <Text type="secondary" className="block text-xs">
                (≈ {costInfo.vnd})
              </Text>
            </div>
          </Descriptions.Item>

          <Descriptions.Item label="Độ trễ xử lý" span={2}>
            {record.latency_ms ? (
              <span className="font-mono">
                {record.latency_ms >= 1000 ? `${(record.latency_ms / 1000).toFixed(2)}s` : `${record.latency_ms}ms`}
              </span>
            ) : (
              "—"
            )}
          </Descriptions.Item>
        </Descriptions>

        {/* Nội dung Prompt (Đầu vào) */}
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-800">Nội dung Prompt (Đầu vào)</span>
              {isPromptJson && <Tag color="cyan">JSON Format</Tag>}
              {record.prompt && (
                <Text type="secondary" className="font-mono text-xs">
                  ({record.prompt.length.toLocaleString()} ký tự)
                </Text>
              )}
            </div>
            {record.prompt && (
              <Button
                size="small"
                type="link"
                icon={<CopyOutlined />}
                onClick={handleCopyPrompt}
                className="h-auto p-0 text-xs"
              >
                Sao chép Prompt
              </Button>
            )}
          </div>

          {record.prompt ? (
            <div className="max-h-[280px] overflow-y-auto rounded-md border border-gray-200 bg-gray-900 p-3 font-mono text-gray-100 text-xs shadow-inner">
              <pre className="m-0 whitespace-pre-wrap break-all font-mono leading-relaxed">{formattedPrompt}</pre>
            </div>
          ) : (
            <div className="rounded-md border border-gray-200 border-dashed bg-gray-50 p-4 text-center text-gray-400 text-xs">
              Không có dữ liệu Prompt được lưu lại
            </div>
          )}
        </div>

        {/* Khung xem AI Output */}
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-800">Kết quả AI Output (Đầu ra)</span>
              {isJson && <Tag color="cyan">JSON Format</Tag>}
              {record.ai_output && (
                <Text type="secondary" className="font-mono text-xs">
                  ({record.ai_output.length.toLocaleString()} ký tự)
                </Text>
              )}
            </div>
            {record.ai_output && (
              <Button
                size="small"
                type="link"
                icon={<CopyOutlined />}
                onClick={handleCopyOutput}
                className="h-auto p-0 text-xs"
              >
                Sao chép Output
              </Button>
            )}
          </div>

          {record.ai_output ? (
            <div className="max-h-[320px] overflow-y-auto rounded-md border border-gray-200 bg-gray-900 p-3 font-mono text-gray-100 text-xs shadow-inner">
              <pre className="m-0 whitespace-pre-wrap break-all font-mono leading-relaxed">{formattedOutput}</pre>
            </div>
          ) : (
            <div className="rounded-md border border-gray-200 border-dashed bg-gray-50 p-4 text-center text-gray-400 text-xs">
              Không có dữ liệu AI Output được lưu lại
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default AiLogDetailModal
